import React from "react";
import { motion } from "framer-motion";
import AnimatedNumber from "./AnimatedNumber";
import { agriBandFor } from "@/data/mockData";

export default function AgriScoreWidget({ score = 82, breakdown = [] }) {
  const radius = 78;
  const C = 2 * Math.PI * radius;
  const dash = (score / 100) * C;
  const band = agriBandFor(score);

  return (
    <div className="card-eco p-6 lg:p-7" data-testid="agri-score-widget">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            AGRI™ Score
          </div>
          <div className="mt-1 text-[12px] text-slate-500">
            AI Governance, Risk & Integrity
          </div>
        </div>
        <span
          className="rounded-full border px-2.5 py-1 text-[11px] font-medium"
          style={{
            color: band.color,
            backgroundColor: `${band.color}1A`,
            borderColor: `${band.color}40`,
          }}
        >
          {band.label}
        </span>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="relative flex items-center justify-center">
          <svg width="200" height="200" viewBox="0 0 200 200" key={score}>
            <defs>
              <linearGradient id={`agriGrad-${score}`} x1="0" y1="0" x2="200" y2="200">
                <stop offset="0%" stopColor="#5B8DEF" />
                <stop offset="100%" stopColor={band.color === "#EF4444" ? "#EF4444" : "#22C55E"} />
              </linearGradient>
            </defs>
            <circle cx="100" cy="100" r={radius} stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
            <motion.circle
              cx="100"
              cy="100"
              r={radius}
              stroke={`url(#agriGrad-${score})`}
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              transform="rotate(-90 100 100)"
              initial={{ strokeDasharray: `0 ${C}` }}
              animate={{ strokeDasharray: `${dash} ${C - dash}` }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
          <div className="absolute flex flex-col items-center">
            <div className="font-mono text-5xl font-light tracking-tight text-white">
              <AnimatedNumber value={score} duration={1400} />
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              / 100
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {breakdown.map((p) => {
            const pct = (p.score / p.weight) * 100;
            return (
              <div key={p.pillar} data-testid={`agri-pillar-${p.pillar.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-slate-300">{p.pillar}</span>
                  <span className="font-mono text-slate-400">
                    {p.score}<span className="text-slate-600"> / {p.weight}</span>
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.04]">
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.1, delay: 0.2, ease: "easeOut" }}
                    style={{
                      background:
                        pct >= 80
                          ? "linear-gradient(90deg,#5B8DEF,#22C55E)"
                          : pct >= 60
                            ? "linear-gradient(90deg,#5B8DEF,#F59E0B)"
                            : "linear-gradient(90deg,#F59E0B,#EF4444)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
